import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HeartHandshake, UserPlus, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  {
    icon: HeartHandshake,
    title: "Refer someone who needs a hand",
    body: "Post a practical request on their behalf — a shopping run, a lift, an odd job. Offers come to YOU, you pick the helper, and you stay the point of contact throughout.",
  },
  {
    icon: UserPlus,
    title: "Invite someone who'd benefit from volunteering",
    body: "Generate a personal invite link. You'll see when they join and when they take their first step — and nothing else about what they do.",
  },
  {
    icon: ShieldCheck,
    title: "Privacy by design",
    body: "We only ever hold a first name and an outward postcode (like SE15). There are no fields for contact details, and none for health or personal circumstances — describe the practical task only.",
  },
];

// Phase 18-A2 item 2: public referrer onboarding for link workers, housing
// officers, librarians and similar professionals.
export default function ForProfessionals() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ name: "", role: "", organisation: "", work_email: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.referrerMe().then(setMe).catch(() => {});
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.referrerApply(form);
      setMe((m) => ({ ...(m || {}), application: { status: "pending" } }));
      toast.success("Application sent — our team will be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your application");
    }
    setBusy(false);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="max-w-4xl mx-auto px-6 py-14" data-testid="for-professionals-page">
      <div className="text-xs uppercase tracking-[0.2em] text-terracotta font-bold">For professionals</div>
      <h1 className="font-display text-4xl sm:text-5xl text-forest mt-3 tracking-tight">
        Connect the people you support to their neighbours
      </h1>
      <p className="text-neutral-600 mt-4 max-w-2xl">
        If you're a link worker, housing officer, librarian — anyone whose day
        job puts you next to people who need a hand or would benefit from
        giving one — CivicHearth gives you two simple, safe rails.
      </p>
      <div className="mt-5 flex items-center gap-4 flex-wrap">
        <Link
          to="/safeguarding"
          data-testid="professionals-safeguarding-link"
          className="inline-flex items-center gap-2 rounded-full bg-forest text-white px-5 py-2.5 text-sm font-medium hover:bg-forest-hover transition-colors"
        >
          <ShieldCheck className="w-4 h-4" /> Read our safeguarding policy
        </Link>
        <Link
          to="/partners#councils"
          data-testid="professionals-councils-link"
          className="text-sm text-forest underline underline-offset-2"
        >
          Representing a council or PCN? See how we work with local authorities
        </Link>
      </div>

      <div className="grid md:grid-cols-3 gap-5 mt-10">
        {STEPS.map((s) => (
          <div key={s.title} className="bg-white border border-neutral-200 rounded-xl p-6">
            <s.icon className="w-6 h-6 text-terracotta" />
            <div className="font-display text-lg text-forest mt-3">{s.title}</div>
            <p className="text-sm text-neutral-600 mt-2">{s.body}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 bg-cream/60 border border-neutral-200 rounded-2xl p-8" data-testid="referrer-apply-section">
        <h2 className="font-display text-lg text-forest">Apply for referrer access</h2>
        {!user ? (
          <p className="text-sm text-neutral-600 mt-2">
            You'll need a CivicHearth account first —{" "}
            <Link to="/login" data-testid="referrer-signin-link" className="text-forest font-medium underline underline-offset-2">sign in or create one</Link>, then come back here.
          </p>
        ) : me?.is_referrer ? (
          <p className="text-sm text-forest mt-2 inline-flex items-center gap-1.5" data-testid="referrer-already">
            <CheckCircle2 className="w-4 h-4" /> You have referrer access — head to your{" "}
            <Link to="/dashboard" className="underline underline-offset-2 font-medium">dashboard</Link>.
          </p>
        ) : me?.application?.status === "pending" ? (
          <p className="text-sm text-neutral-600 mt-2" data-testid="referrer-pending">
            Your application is with our team — we'll let you know soon.
          </p>
        ) : (
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 mt-4 max-w-2xl">
            <Input data-testid="referrer-name" required placeholder="Your name" value={form.name} onChange={set("name")} />
            <Input data-testid="referrer-role" required placeholder="Role (e.g. social prescribing link worker)" value={form.role} onChange={set("role")} />
            <Input data-testid="referrer-organisation" required placeholder="Organisation" value={form.organisation} onChange={set("organisation")} />
            <Input data-testid="referrer-work-email" required type="email" placeholder="Work email" value={form.work_email} onChange={set("work_email")} />
            <div className="sm:col-span-2">
              <Button data-testid="referrer-apply-submit" type="submit" disabled={busy} className="rounded-full bg-forest hover:bg-forest-hover h-11 px-8">
                {busy ? "Sending…" : "Apply for access"}
              </Button>
              <p className="text-xs text-neutral-500 mt-2">A person on our team reviews every application.</p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
